import type { BadgeProps } from '@/components/ui/badge';
import type {
  ActivityKind,
  AssetType,
  BillingModel,
  BillingTrigger,
  ContractStatus,
  ContractType,
  MilestoneStatus,
  ProjectHealth,
  ProjectStatus,
  ProjectType,
  RiskStatus,
  SprintStatus,
  TicketPriority,
  TicketSource,
  TicketStatus,
  TicketType,
  TimesheetStatus,
  WorkItemPriority,
  WorkItemStatus,
  WorkItemType,
} from './types';

type Variant = BadgeProps['variant'];

// ── Delivery ─────────────────────────────────────────────────────────────────

export const PROJECT_TYPE_LABELS: Record<ProjectType, string> = {
  CUSTOM_DEVELOPMENT: 'Custom development',
  IMPLEMENTATION: 'Implementation',
  RETAINER: 'Retainer',
  INTERNAL_PRODUCT: 'Internal product',
  SUPPORT: 'Support',
};

export const BILLING_MODEL_LABELS: Record<BillingModel, string> = {
  FIXED_PRICE: 'Fixed price',
  TIME_AND_MATERIAL: 'Time and material',
  RETAINER: 'Retainer',
  MILESTONE: 'Milestone',
};

export const PROJECT_STATUS_LABELS: Record<ProjectStatus, string> = {
  PLANNING: 'Planning',
  ACTIVE: 'Active',
  ON_HOLD: 'On hold',
  AT_RISK: 'At risk',
  COMPLETED: 'Completed',
  CANCELLED: 'Cancelled',
};

export const PROJECT_STATUS_VARIANT: Record<ProjectStatus, Variant> = {
  PLANNING: 'secondary',
  ACTIVE: 'default',
  ON_HOLD: 'outline',
  AT_RISK: 'warning',
  COMPLETED: 'success',
  CANCELLED: 'outline',
};

export const HEALTH_LABELS: Record<ProjectHealth, string> = {
  GREEN: 'On track',
  AMBER: 'Needs attention',
  RED: 'In trouble',
};

export const HEALTH_DOT: Record<ProjectHealth, string> = {
  GREEN: 'bg-emerald-500',
  AMBER: 'bg-amber-500',
  RED: 'bg-destructive',
};

export const MILESTONE_STATUS_LABELS: Record<MilestoneStatus, string> = {
  PENDING: 'Not started',
  IN_PROGRESS: 'In progress',
  COMPLETED: 'Completed',
  APPROVED: 'Signed off',
  INVOICED: 'Invoiced',
};

export const MILESTONE_STATUS_VARIANT: Record<MilestoneStatus, Variant> = {
  PENDING: 'outline',
  IN_PROGRESS: 'secondary',
  COMPLETED: 'default',
  APPROVED: 'success',
  INVOICED: 'success',
};

export const BILLING_TRIGGER_LABELS: Record<BillingTrigger, string> = {
  ON_COMPLETION: 'When completed',
  ON_APPROVAL: 'When the client signs off',
  ON_DATE: 'On a fixed date',
  MANUAL: 'By hand',
};

export const WORK_ITEM_TYPE_LABELS: Record<WorkItemType, string> = {
  EPIC: 'Epic',
  STORY: 'Story',
  TASK: 'Task',
  BUG: 'Bug',
  IMPROVEMENT: 'Improvement',
  SPIKE: 'Spike',
  CHORE: 'Chore',
};

export const WORK_ITEM_TYPE_COLOUR: Record<WorkItemType, string> = {
  EPIC: 'text-violet-600',
  STORY: 'text-emerald-600',
  TASK: 'text-sky-600',
  BUG: 'text-destructive',
  IMPROVEMENT: 'text-teal-600',
  SPIKE: 'text-amber-600',
  CHORE: 'text-slate-500',
};

export const WORK_ITEM_STATUS_LABELS: Record<WorkItemStatus, string> = {
  BACKLOG: 'Backlog',
  TODO: 'To do',
  IN_PROGRESS: 'In progress',
  CODE_REVIEW: 'Code review',
  QA: 'QA',
  UAT: 'UAT',
  DONE: 'Done',
  DEPLOYED: 'Deployed',
};

export const WORK_ITEM_PRIORITY_LABELS: Record<WorkItemPriority, string> = {
  CRITICAL: 'Critical',
  HIGH: 'High',
  MEDIUM: 'Medium',
  LOW: 'Low',
};

export const WORK_ITEM_PRIORITY_VARIANT: Record<WorkItemPriority, Variant> = {
  CRITICAL: 'destructive',
  HIGH: 'warning',
  MEDIUM: 'secondary',
  LOW: 'outline',
};

export const SPRINT_STATUS_LABELS: Record<SprintStatus, string> = {
  PLANNED: 'Planned',
  ACTIVE: 'Running',
  COMPLETED: 'Completed',
};

export const RISK_STATUS_LABELS: Record<RiskStatus, string> = {
  OPEN: 'Open',
  MITIGATING: 'Being mitigated',
  CLOSED: 'Closed',
  OCCURRED: 'It happened',
};

export const ACTIVITY_KIND_LABELS: Record<ActivityKind, string> = {
  DEVELOPMENT: 'Development',
  DESIGN: 'Design',
  QA: 'QA',
  MEETING: 'Meeting',
  SUPPORT: 'Support',
  DEPLOYMENT: 'Deployment',
  DOCUMENTATION: 'Documentation',
  RESEARCH: 'Research',
  INTERNAL: 'Internal',
  TRAINING: 'Training',
  PRE_SALES: 'Pre-sales',
  LEAVE: 'Leave',
  HOLIDAY: 'Public holiday',
  BENCH: 'Bench',
};

export const TIMESHEET_STATUS_LABELS: Record<TimesheetStatus, string> = {
  DRAFT: 'Not submitted',
  SUBMITTED: 'Awaiting approval',
  APPROVED: 'Approved',
  REJECTED: 'Sent back',
};

export const TIMESHEET_STATUS_VARIANT: Record<TimesheetStatus, Variant> = {
  DRAFT: 'outline',
  SUBMITTED: 'secondary',
  APPROVED: 'success',
  REJECTED: 'destructive',
};

// ── Service desk ─────────────────────────────────────────────────────────────

export const TICKET_TYPE_LABELS: Record<TicketType, string> = {
  INCIDENT: 'Incident',
  BUG: 'Bug',
  SERVICE_REQUEST: 'Service request',
  QUESTION: 'Question',
  CHANGE_REQUEST: 'Change request',
  ENHANCEMENT: 'Enhancement',
};

export const TICKET_PRIORITY_LABELS: Record<TicketPriority, string> = {
  URGENT: 'Urgent',
  HIGH: 'High',
  NORMAL: 'Normal',
  LOW: 'Low',
};

export const TICKET_PRIORITY_VARIANT: Record<TicketPriority, Variant> = {
  URGENT: 'destructive',
  HIGH: 'warning',
  NORMAL: 'secondary',
  LOW: 'outline',
};

export const TICKET_STATUS_LABELS: Record<TicketStatus, string> = {
  NEW: 'New',
  OPEN: 'Open',
  PENDING_CUSTOMER: 'Waiting on client',
  PENDING_THIRD_PARTY: 'Waiting on supplier',
  IN_PROGRESS: 'In progress',
  RESOLVED: 'Resolved',
  CLOSED: 'Closed',
  REOPENED: 'Reopened',
};

export const TICKET_STATUS_VARIANT: Record<TicketStatus, Variant> = {
  NEW: 'default',
  OPEN: 'secondary',
  PENDING_CUSTOMER: 'outline',
  PENDING_THIRD_PARTY: 'outline',
  IN_PROGRESS: 'secondary',
  RESOLVED: 'success',
  CLOSED: 'outline',
  REOPENED: 'warning',
};

export const TICKET_SOURCE_LABELS: Record<TicketSource, string> = {
  PORTAL: 'Client portal',
  EMAIL: 'Email',
  WHATSAPP: 'WhatsApp',
  PHONE: 'Phone',
  INTERNAL: 'Raised internally',
  API: 'API',
  MONITORING: 'Monitoring alert',
};

export const CONTRACT_TYPE_LABELS: Record<ContractType, string> = {
  AMC: 'AMC',
  SUPPORT_RETAINER: 'Support retainer',
  MANAGED_SERVICE: 'Managed service',
  WARRANTY: 'Warranty',
};

export const CONTRACT_STATUS_LABELS: Record<ContractStatus, string> = {
  DRAFT: 'Draft',
  ACTIVE: 'Active',
  EXPIRING: 'Expiring',
  EXPIRED: 'Expired',
  RENEWED: 'Renewed',
  TERMINATED: 'Terminated',
};

export const CONTRACT_STATUS_VARIANT: Record<ContractStatus, Variant> = {
  DRAFT: 'outline',
  ACTIVE: 'success',
  EXPIRING: 'warning',
  EXPIRED: 'destructive',
  RENEWED: 'secondary',
  TERMINATED: 'outline',
};

export const ASSET_TYPE_LABELS: Record<AssetType, string> = {
  SERVER: 'Server',
  VM: 'Virtual machine',
  DOMAIN: 'Domain',
  SSL_CERTIFICATE: 'SSL certificate',
  THIRD_PARTY_LICENCE: 'Third-party licence',
  DEVICE: 'Device',
  OTHER: 'Other',
};

// ── Formatting ───────────────────────────────────────────────────────────────

/** 90 -> "1h 30m". The grid stores minutes; people read hours. */
export function formatMinutes(minutes: number): string {
  const sign = minutes < 0 ? '-' : '';
  const abs = Math.abs(Math.round(minutes));
  const hours = Math.floor(abs / 60);
  const rest = abs % 60;
  if (hours === 0) return `${sign}${rest}m`;
  if (rest === 0) return `${sign}${hours}h`;
  return `${sign}${hours}h ${rest}m`;
}

/** "7.5" -> 450. Accepts "7:30" and "7h 30m" too, because people type those. */
export function parseHoursInput(value: string): number | null {
  const trimmed = value.trim().toLowerCase();
  if (!trimmed) return null;

  const colon = trimmed.match(/^(\d+):([0-5]\d)$/);
  if (colon) return Number(colon[1]) * 60 + Number(colon[2]);

  const hm = trimmed.match(/^(?:(\d+(?:\.\d+)?)\s*h)?\s*(?:(\d+)\s*m)?$/);
  if (hm && (hm[1] || hm[2])) {
    return Math.round(Number(hm[1] ?? 0) * 60) + Number(hm[2] ?? 0);
  }

  const decimal = Number(trimmed);
  return Number.isFinite(decimal) && decimal >= 0 ? Math.round(decimal * 60) : null;
}

/** The colour an SLA countdown shows, and what it means in words. */
export const SLA_COLOUR_CLASS: Record<'GREEN' | 'AMBER' | 'RED', string> = {
  GREEN: 'text-emerald-600',
  AMBER: 'text-amber-600',
  RED: 'text-destructive',
};

export const SLA_DOT_CLASS: Record<'GREEN' | 'AMBER' | 'RED', string> = {
  GREEN: 'bg-emerald-500',
  AMBER: 'bg-amber-500',
  RED: 'bg-destructive',
};

/**
 * Turns a due date into how long is left, in plain words.
 *
 * Deliberately vague past a day: "in 3 days" is what someone needs, and
 * "in 3 days, 4 hours and 12 minutes" is noise on a queue of forty rows.
 */
export function timeUntil(iso: string | null | undefined, now = Date.now()): string {
  if (!iso) return '—';
  const diff = new Date(iso).getTime() - now;
  const overdue = diff < 0;
  const minutes = Math.round(Math.abs(diff) / 60_000);

  const phrase =
    minutes < 60
      ? `${minutes}m`
      : minutes < 60 * 24
        ? `${Math.round(minutes / 60)}h`
        : `${Math.round(minutes / (60 * 24))}d`;

  return overdue ? `${phrase} over` : `in ${phrase}`;
}
